import { useState, useEffect } from "react";
import Tag from "../tags/Tag";
import ComboBox from "../core/ComboBox";
import { useAuthInfo } from "../../context/AuthContext";
import useTriggeredAPICall from "../../hooks/fetch-hooks/useTriggeredAPICall";
import api from "../../services/api";
import {
  confirmToast,
  errorToast,
  successfulToast,
} from "../../util/toastNotifications";

const FormFieldProjects = ({
  selectedProjects,
  handleProjectRemove,
  handleAddProjectToContract,
  contractId,
  hideLabel = false,
  ...props
}) => {
  const { userInfo } = useAuthInfo();
  const { fetchData } = useTriggeredAPICall();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [allProjects, setAllProjects] = useState([]);

  useEffect(() => {
    if (isModalOpen) {
      fetchData(`/projects?org_id=${userInfo.org_id}`, "GET")
        .then((data) => {
          const selectedProjectIds = (selectedProjects || []).map(
            (p) => p.project_id
          );
          const availableProjects = (data.results || [])
            .filter((project) => !selectedProjectIds.includes(project.project_id))
            .sort((a, b) => {
              const aHasContract =
                a.contract_id && a.contract_id !== contractId;
              const bHasContract =
                b.contract_id && b.contract_id !== contractId;
              if (aHasContract === bHasContract) return 0;
              return aHasContract ? 1 : -1;
            });
          setAllProjects(availableProjects);
        })
        .catch((err) => console.error("Fetch Projects", err));
    }
  }, [isModalOpen, fetchData, userInfo.org_id, contractId, selectedProjects]);

  const handleOpenAddProject = (e) => {
    e.stopPropagation();
    setIsModalOpen(true);
  };

  const formatDataForDisplay = (project) => {
    if (project.contract_id && project.contract_id !== contractId) {
      return `${project.name} (Already assigned)`;
    }
    return `${project.name}`;
  };

  const handleProjectSelect = async (projectId) => {
    if (!contractId) {
      console.error("Contract ID is required");
      return;
    }

    try {
      const selectedProject = allProjects.find(
        (project) => project.project_id === projectId
      );
      let existingContractId = selectedProject?.contract_id;
      let shouldShowSuccessToast = true;

      if (!existingContractId) {
        const projectDetails = await api.project.getProjectById(projectId);
        existingContractId =
          projectDetails?.contract_id || projectDetails?.contract?.contract_id;
      }

      if (existingContractId && existingContractId !== contractId) {
        const shouldReassign = await confirmToast(
          "This project already has a contract assigned. Reassign it to this contract?"
        );
        if (!shouldReassign) {
          return;
        }
        shouldShowSuccessToast = false;
      }

      await api.project.updateProject(projectId, {
        contract_id: contractId,
      });

      const projectResponse = await api.project.getProjectById(projectId);
      const newProject = {
        project_id: projectResponse.project_id,
        name: projectResponse.name,
        color: projectResponse.color || "#6e747a",
      };

      if (handleAddProjectToContract) {
        handleAddProjectToContract(newProject, existingContractId);
      }
      window.dispatchEvent(
        new CustomEvent("contract-projects-updated", {
          detail: {
            contractIds: existingContractId
              ? [existingContractId, contractId]
              : [contractId],
          },
        })
      );
      if (shouldShowSuccessToast) {
        successfulToast("Project added to contract");
      }
    } catch (err) {
      console.error("Add Project to Contract", err);
      errorToast("Unable to add project to contract");
    }
  };

  const handleContainerClick = (e) => {
    e.stopPropagation();
    if (props.onClick) {
      props.onClick(e);
    }
  };

  return (
    <div className="input-container flex-rows" onClick={handleContainerClick}>
      {handleAddProjectToContract && !hideLabel && (
        <label htmlFor={name}>Projects</label>
      )}

      <div className="input-tag-container" onClick={(e) => e.stopPropagation()}>
        {selectedProjects?.map((project) => (
          <Tag
            tag={{
              tag_id: project.project_id,
              name: project.name,
              color: project.color || "#6c757d",
            }}
            key={project.project_id}
            handleTagRemove={handleProjectRemove}
          />
        ))}
        {isModalOpen ? (
          <ComboBox
            data={allProjects}
            selected={selectedProjects}
            valueKey="project_id"
            placeholder="Search project..."
            searchTopic="project"
            formatData={formatDataForDisplay}
            handleSelect={handleProjectSelect}
            onRequestClose={() => setIsModalOpen(false)}
            inline={true}
          />
        ) : (
          handleAddProjectToContract != null && (
            <div
              className="tag add-tag"
              key="placeholder"
              onClick={handleOpenAddProject}
            >
              + Add Project
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default FormFieldProjects;
